import { SynchTripsExpReportDTO } from 'business/models/Models';
import RMTable from 'components/customComponents/Controls/RMTable';
import { FmlxButton } from 'fmlx-common-ui';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFetchSynchTripsByExpId, useSynchTripsExpReport } from './hooks';
import './synchrotronTrip.scss';
import { TableColumns } from './utils';

interface IProps {
  expId: number;
  closeReportOptionsModal?: () => void;
}

function SynchTripsExpReportOptions(props: IProps) {
  const { expId, closeReportOptionsModal } = props;
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const hasInitializedSelection = useRef(false);
  const { synchTrips, loading } = useFetchSynchTripsByExpId(expId);

  const { openSynchTripsExpReport, saveSynchTripsExpReport } = useSynchTripsExpReport();

  const totalRowCount = synchTrips?.length ?? 0;
  const selectAll = totalRowCount > 0 && selectedIds.size === totalRowCount;

  const handleSelectChange = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(synchTrips?.map((t) => t.id) ?? []));
    }
  };

  const tableCols: TableColumns[] = [
    {
      accessor: 'synchTripName',
      Header: 'Name',
    },
    {
      accessor: 'selected',
      Header: 'Selected',
      className: 'synch_name',
      Cell: ({ cell }: any) => (
        <input
          type="checkbox"
          id={`synchtripsexpreportoptions-tablecols-input-select-${cell.row.original.id}`}
          className="h-4 w-4 rounded bg-red-100"
          onChange={() => handleSelectChange(cell.row.original.id)}
          checked={Boolean(cell.value)}
        />
      ),
    },
  ];

  const tableData = useMemo(
    () =>
      synchTrips?.map((trip) => ({
        id: trip.id,
        synchTripName: trip.synchTripName,
        selected: selectedIds.has(trip.id) ? 1 : 0,
      })),
    [synchTrips, selectedIds],
  );

  const handleOpenSynchTripReport = async () => {
    const reportDto: SynchTripsExpReportDTO = new SynchTripsExpReportDTO({
      expId,
      synchTripIds: Array.from(selectedIds),
    });
    openSynchTripsExpReport(reportDto);
    closeReportOptionsModal();
  };

  const handleSaveSynchTripReport = async () => {
    const reportDto: SynchTripsExpReportDTO = new SynchTripsExpReportDTO({
      expId,
      synchTripIds: Array.from(selectedIds),
    });
    saveSynchTripsExpReport(reportDto);
    closeReportOptionsModal();
  };

  useEffect(() => {
    hasInitializedSelection.current = false;
  }, [expId]);

  useEffect(() => {
    if (synchTrips?.length && !hasInitializedSelection.current) {
      hasInitializedSelection.current = true;
      setSelectedIds(new Set(synchTrips.map((t) => t.id)));
    }
  }, [synchTrips]);

  const getContent = () =>
    tableData?.length ? (
      <>
        <span id="synchtripsexpreportoptions-content-select-message">
          Please select synchrotron trip you want to include into report
        </span>
        <div id="synchtripsexpreportoptions-select-all-wrapper">
          <input
            type="checkbox"
            id="synchtripsexpreportoptions-checkbox-select-all"
            className="h-4 w-4 rounded bg-red-100"
            onChange={handleSelectAll}
            checked={selectAll}
          />
          <label
            id="synchtripsexpreportoptions-label-select-all"
            htmlFor="select-all"
            className="mx-1"
          >
            Select all synchrotron trips
          </label>
        </div>
        <div id="synchtripsexpreportoptions-table-wrapper">
          <RMTable columns={tableCols} data={tableData || []} tableClass="w-full" />
        </div>
        <div
          id="synch-trip-report-option-footer"
          className="flex w-full justify-end my-3 gap-[10px]"
        >
          <FmlxButton
            id="synchtripsexpreportoptions-button-cancel"
            variant={FmlxButton.Variant.PLAIN}
            onClick={closeReportOptionsModal}
            type={FmlxButton.Type.BASIC}
            label="Cancel"
          />
          <FmlxButton
            id="synchtripsexpreportoptions-button-preview"
            variant={FmlxButton.Variant.OUTLINE}
            onClick={handleOpenSynchTripReport}
            disabled={!selectedIds?.size}
            label="Preview"
          />
          <FmlxButton
            id="synchtripsexpreportoptions-button-save"
            onClick={handleSaveSynchTripReport}
            disabled={!selectedIds?.size}
            label="Save"
          />
        </div>
      </>
    ) : (
      <span id="synchtripsexpreportoptions-empty-message" className="mx-auto mb-8">
        No Crystals assigned to any Synchrotron Trips.
      </span>
    );

  return (
    <div id="synchtripsexpreportoptions-col-wrapper" className="flex flex-col">
      {loading ? (
        <span id="synchtripsexpreportoptions-loading-message" className="mx-auto mb-8">
          Loading...
        </span>
      ) : (
        getContent()
      )}
    </div>
  );
}

export default SynchTripsExpReportOptions;
